import { getTemplateGeneratorBaseUrl } from "src/config/Endpoints";
import i18n from "src/i18n/config";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

export type WhatsAppMediaType = "image" | "video" | "document";
export type WhatsAppMappingType = "column" | "static" | "date";

export interface WhatsAppApiResult {
  success: boolean;
  connected?: boolean | null;
  message?: string | null;
  error_code?: string | null;
  errors?: string[] | null;
}

export interface WhatsAppApiButton {
  index: number;
  type: string;
  kind?: string | null;
  text?: string | null;
  url?: string | null;
  dynamic?: boolean | null;
}

export interface WhatsAppApiTemplateSummary {
  variable_keys?: string[] | null;
  header_format?: string | null;
  header_text?: string | null;
  media_attachment?: string | null;
  body_text?: string | null;
  footer_text?: string | null;
  buttons?: WhatsAppApiButton[] | null;
}

export interface WhatsAppApiTemplate {
  id: string;
  name: string;
  language?: string | null;
  display_status?: string | null;
  rejected_reason?: string | null;
  used_in_campaign?: { campaign_name?: string | null } | null;
  can_use?: boolean | null;
  unsupported_reasons?: string[] | null;
  meta_manage_url?: string | null;
  summary?: WhatsAppApiTemplateSummary | null;
}

export interface WhatsAppTemplatesResponse extends WhatsAppApiResult {
  meta_manage_url?: string | null;
  templates?: WhatsAppApiTemplate[] | null;
}

export interface WhatsAppAudienceColumnsResponse extends WhatsAppApiResult {
  columns?: string[] | null;
  row_count?: number | null;
  invalid_phone_count?: number | null;
}

export interface WhatsAppUploadedMedia {
  type: WhatsAppMediaType;
  ref: string;
  mime_type: string;
  file_name: string;
  size_bytes?: number | null;
}

export interface WhatsAppMediaResponse extends WhatsAppApiResult {
  media?: WhatsAppUploadedMedia | null;
}

export interface WhatsAppVariableMapping {
  key: string;
  type: WhatsAppMappingType;
  value: string;
  fallback?: string;
  timezone?: string;
}

export type WhatsAppHeaderMedia = Pick<
  WhatsAppUploadedMedia,
  "type" | "ref" | "mime_type" | "file_name"
>;

export interface WhatsAppPreviewButton {
  index: number;
  type: string;
  text?: string | null;
  url?: string | null;
}

export interface WhatsAppPreviewSection {
  header_text?: string | null;
  body?: string | null;
  footer?: string | null;
  buttons?: WhatsAppPreviewButton[] | null;
}

export interface WhatsAppPreviewResponse extends WhatsAppApiResult {
  parameters?: WhatsAppPreviewSection | null;
  example?: WhatsAppPreviewSection | null;
}

export interface WhatsAppSendResponse extends WhatsAppApiResult {
  campaign_id?: number | null;
  status?: string | null;
  total_recipients?: number | null;
}

interface WhatsAppTemplateTarget {
  template_name: string;
  language_code: string;
  list_names: string[];
  variable_mappings: WhatsAppVariableMapping[];
}

export type WhatsAppPreviewPayload = WhatsAppTemplateTarget;

export interface WhatsAppSendPayload extends WhatsAppTemplateTarget {
  campaign_name: string;
  header_media?: WhatsAppHeaderMedia;
  scheduled_for?: string;
}

const getBaseUrl = () => `${getTemplateGeneratorBaseUrl()}/whatsapp`;

const getTenantId = (): string => {
  const tenantId = localStorage.getItem("tenant_id");
  if (!tenantId) {
    throw new Error("Missing tenant_id in localStorage");
  }
  return tenantId;
};

const buildUrl = (path: string, params: URLSearchParams): string => {
  params.set("lang", getApiLangFromI18n(i18n));
  return `${getBaseUrl()}${path}?${params.toString()}`;
};

const postJson = async <T,>(path: string, body: object, signal?: AbortSignal): Promise<T> => {
  const response = await fetch(buildUrl(path, new URLSearchParams()), {
    method: "POST",
    headers: buildAuthHeaders(),
    body: JSON.stringify({ tenant_id: Number(getTenantId()), source: "whatsapp", ...body }),
    signal,
  });
  return handleJsonResponse<T>(response);
};

export const WhatsAppCampaignAPI = {
  async fetchTemplates(): Promise<WhatsAppTemplatesResponse> {
    const params = new URLSearchParams({ tenant_id: getTenantId(), source: "whatsapp" });
    const response = await fetch(buildUrl("/templates", params), {
      headers: buildAuthHeaders(),
    });
    return handleJsonResponse<WhatsAppTemplatesResponse>(response);
  },

  async fetchAudienceColumns(listNames: string[]): Promise<WhatsAppAudienceColumnsResponse> {
    const params = new URLSearchParams({ tenant_id: getTenantId() });
    listNames.forEach(listName => params.append("list_names", listName));
    const response = await fetch(buildUrl("/audience-columns", params), {
      headers: buildAuthHeaders(),
    });
    return handleJsonResponse<WhatsAppAudienceColumnsResponse>(response);
  },

  async uploadMedia(mediaType: WhatsAppMediaType, file: File): Promise<WhatsAppMediaResponse> {
    const formData = new FormData();
    formData.append("tenant_id", getTenantId());
    formData.append("media_type", mediaType);
    formData.append("file", file);
    const { Authorization } = buildAuthHeaders();
    const response = await fetch(buildUrl("/media", new URLSearchParams()), {
      method: "POST",
      headers: { Authorization },
      body: formData,
    });
    return handleJsonResponse<WhatsAppMediaResponse>(response);
  },

  fetchPreview(payload: WhatsAppPreviewPayload, signal?: AbortSignal) {
    return postJson<WhatsAppPreviewResponse>("/preview", payload, signal);
  },

  sendCampaign(payload: WhatsAppSendPayload) {
    return postJson<WhatsAppSendResponse>("/send-campaign", payload);
  },
};
